import { Copy, Mail, MessageCircle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { getErrorMessage } from "@/lib/errors";
import { VendorContactDialog } from "@/components/procurement/VendorContactDialog";

const dmy = (d) => (d ? d.split("-").reverse().join("-") : "");
const stamp = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

// 10 digits -> India (+91); anything else is used as typed (digits only, country code included).
export function digitsForWhatsApp(phone) {
  const d = String(phone || "").replace(/\D/g, "");
  if (d.length === 10) return `91${d}`;
  if (d.length === 11 && d.startsWith("0")) return `91${d.slice(1)}`;
  return d;
}

export async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch {
    const ta = document.createElement("textarea");
    ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    let ok = false;
    try { ok = document.execCommand("copy"); } catch { ok = false; }
    document.body.removeChild(ta);
    return ok;
  }
}

/** The message sent to a vendor with their private quote link. */
export function quoteMessage({ rfq, vendor, link, companyName }) {
  return `Dear ${vendor?.contact_person || vendor?.name || "Sir/Madam"},\n\n${companyName || "We"} request your quotation ${rfq.number}` +
    `${rfq.due_date ? ` (please reply by ${dmy(rfq.due_date)})` : ""}.\n` +
    `Please open this link and enter your price for each item:\n${link}\n\nThank you.`;
}

/** Per-vendor quote link on an RFQ: status badge, the vendor's phone/email, and Copy / WhatsApp / Email once the RFQ is sent. */
export function ShareQuoteLink({ rfq, invite, vendor, companyName, canEditContact, onVendorUpdated }) {
  if (!invite?.access_token) return null;
  const sent = rfq.status === "SENT";
  const link = `${window.location.origin}/rfq/${invite.access_token}`;
  const phone = digitsForWhatsApp(vendor?.phone);
  const email = (vendor?.email || "").trim();
  const message =
    `Dear ${vendor?.contact_person || vendor?.name || "Sir/Madam"},\n\n${companyName || "We"} request your quotation ${rfq.number}` +
    `${rfq.due_date ? ` (please reply by ${dmy(rfq.due_date)})` : ""}.\n` +
    `Please open this link and enter your price for each item:\n${link}\n\nThank you.`;

  const badge = invite.submitted_at
    ? { cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400", text: `Quoted via link · ${stamp(invite.submitted_at)}` }
    : invite.viewed_at
      ? { cls: "bg-sky-500/10 text-sky-700 dark:text-sky-400", text: `Opened · ${stamp(invite.viewed_at)}` }
      : sent ? { cls: "bg-muted text-muted-foreground", text: "Link not opened yet" } : null;

  const copy = async () => {
    if (await copyText(link)) toast.success("Quote link copied");
    else toast.error("Could not copy. Select the link and copy it by hand.");
  };
  const allowRevision = async () => {
    try {
      await api.post(`/procurement/rfqs/${rfq.id}/vendors/${invite.vendor_id}/reopen-quote`);
      toast.success(`${vendor?.name || "Vendor"} can now revise their quote`);
      onVendorUpdated?.();
    } catch (err) { toast.error(getErrorMessage(err)); }
  };
  const wa = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  const mail = `mailto:${email}?subject=${encodeURIComponent(`Request for quotation ${rfq.number}`)}&body=${encodeURIComponent(message)}`;
  const id = `${rfq.number}-${invite.vendor_id}`;

  return (
    <>
      {badge && <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${badge.cls}`} data-testid={`rfq-link-status-${id}`}>{badge.text}</span>}
      {vendor && (
        <span className="flex items-center gap-1 text-[11px] text-muted-foreground" data-testid={`rfq-contact-${id}`}>
          <span className={phone ? "" : "text-amber-600 dark:text-amber-400"}>{vendor.phone || "No phone"}</span>
          <span aria-hidden>·</span>
          <span className={email ? "" : "text-amber-600 dark:text-amber-400"}>{email || "No email"}</span>
          {canEditContact && <VendorContactDialog vendor={vendor} onSaved={onVendorUpdated} testId={`rfq-edit-contact-${id}`} />}
        </span>
      )}
      {sent && invite.submitted_at && canEditContact && (
        <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={allowRevision} title="Let this vendor change the quote they sent through the link" data-testid={`rfq-allow-revision-${id}`}><Undo2 className="h-3.5 w-3.5" /> Allow revision</Button>
      )}
      {sent && (
        <span className="flex items-center gap-1">
          <Button size="sm" variant="outline" onClick={copy} title="Copy the vendor's private quote link" data-testid={`rfq-copy-link-${id}`}><Copy className="h-3.5 w-3.5" /> Link</Button>
          {phone
            ? <Button size="sm" variant="outline" asChild title={`Send on WhatsApp to ${vendor.phone}`}><a href={wa} target="_blank" rel="noreferrer" data-testid={`rfq-whatsapp-${id}`}><MessageCircle className="h-3.5 w-3.5" /></a></Button>
            : <Button size="sm" variant="outline" disabled title="Add the vendor's phone number first" data-testid={`rfq-whatsapp-${id}`}><MessageCircle className="h-3.5 w-3.5" /></Button>}
          {email
            ? <Button size="sm" variant="outline" asChild title={`Email ${email}`}><a href={mail} data-testid={`rfq-email-${id}`}><Mail className="h-3.5 w-3.5" /></a></Button>
            : <Button size="sm" variant="outline" disabled title="Add the vendor's email first" data-testid={`rfq-email-${id}`}><Mail className="h-3.5 w-3.5" /></Button>}
        </span>
      )}
    </>
  );
}
